import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ProjectChatContext } from "../components/projects/ProjectChatContext";
import { ProjectSourceReader } from "../components/projects/ProjectSourceReader";
import { api } from "../lib/api";
import { streamChat } from "../lib/sse";
import ConversationMode from "../components/ConversationMode";
import CitationDrawer from "../components/CitationDrawer";
import DocumentReader from "../components/DocumentReader";
import ThinkingStage from "../components/ThinkingStage";
import WakeWordListener from "../components/WakeWordListener";
import { ChatSidebar } from "../components/chat/ChatSidebar";
import { ChatHeader } from "../components/chat/ChatHeader";
import { ChatWelcome, MessageList } from "../components/chat/MessageList";
import { ChatComposer } from "../components/chat/ChatComposer";
import { useRecorder } from "../hooks/useRecorder";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useAttachments } from "../hooks/useAttachments";
import { History, X } from "lucide-react";

const DEFAULT_FLAGS = { voice_always_on: true, voice_wake_word: false, voice_auto_tts: true, chat_export: true };

function welcomeMessages(org) {
  return org?.welcome_message ? [{ role: "assistant", content: org.welcome_message, sources: [] }] : [];
}

export default function Chat() {
  const location = useLocation();
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(null);
  const [contextIds, setContextIds] = useState({ interview_id: location.state?.interview_id || null, application_id: location.state?.application_id || null });
  const [allowProjectWrite, setAllowProjectWrite] = useState(false);
  const [projectCitation, setProjectCitation] = useState(null);
  const { org } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const attachments = useAttachments("aria");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(location.state?.prompt || "");
  const [search, setSearch] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [orbState, setOrbState] = useState("idle");
  const [voiceMode, setVoiceMode] = useState(false);
  const [docs, setDocs] = useState([]);
  const [docFilter, setDocFilter] = useState("all");
  const [conversationOpen, setConversationOpen] = useState(false);
  const [citeDrawer, setCiteDrawer] = useState({ open: false, name: null, citations: [] });
  const [docReader, setDocReader] = useState({ open: false, docId: null, citation: null });
  const [wakeStatus, setWakeStatus] = useState("off");
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const audioRef = useRef(null);
  const endRef = useRef(null);
  const { recording, toggle: toggleRecording } = useRecorder({ onText: setInput, onState: setOrbState });

  // Conversation Mode opens only on explicit user action (button / wake word).
  useEffect(() => {
    sessionStorage.removeItem("aria_jarvis_autoconv");
    const handler = () => setConversationOpen(true);
    window.addEventListener("aria-autoconv", handler);
    return () => window.removeEventListener("aria-autoconv", handler);
  }, []);

  useEffect(() => {
    api.get("/settings/org/feature-flags").then(({ data }) => setFlags(prev => ({ ...prev, ...data.values })))
      .catch((e) => console.warn("[chat] feature-flags fetch failed:", e?.message || e));
  }, []);

  const loadConversations = useCallback(async () => {
    try { const { data } = await api.get("/chat/history"); setConversations(data); }
    catch (e) { console.warn("[chat] loadConversations failed:", e?.message || e); }
  }, []);
  const loadDocs = useCallback(async () => {
    try { const { data } = await api.get("/documents"); setDocs(data.filter(d => d.status === "ready")); }
    catch (e) { console.warn("[chat] loadDocs failed:", e?.message || e); }
  }, []);
  useEffect(() => { loadConversations(); loadDocs(); }, [loadConversations, loadDocs]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  // Welcome message for a fresh conversation
  useEffect(() => {
    if (!activeId && org?.welcome_message) setMessages(m => (m.length === 0 ? welcomeMessages(org) : m));
  }, [org, activeId]);

  const openConversation = async (id) => {
    if (streaming || attachments.uploading) return;
    attachments.clear(); setHistoryOpen(false);
    setActiveId(id);
    const { data } = await api.get(`/chat/${id}`);
    setContextIds({ interview_id: data.interview_id || null, application_id: data.application_id || null });
    setProjectId(data.project_id || null); setAllowProjectWrite(false);
    let feedbackMap = {};
    try { feedbackMap = (await api.get(`/chat/${id}/feedback`)).data || {}; }
    catch (e) { console.warn("[ARIA] feedback hydrate failed (non-fatal)", e); }
    setMessages((data.messages || []).map((m, idx) => ({ ...m, feedback: feedbackMap[String(idx)]?.value || null })));
  };

  const newConversation = () => { if (streaming || attachments.uploading) return; attachments.clear(); setHistoryOpen(false); setActiveId(null); setMessages(welcomeMessages(org)); setContextIds({ interview_id: null, application_id: null }); setAllowProjectWrite(false); };

  const deleteConv = async (id, e) => {
    e.stopPropagation();
    if (streaming || attachments.uploading) return;
    if (!confirm("Delete this conversation?")) return;
    await api.delete(`/chat/${id}`);
    if (activeId === id) newConversation();
    loadConversations();
  };

  const playTTS = async (text) => {
    try {
      const { data } = await api.post("/tts", { text }, { responseType: "blob" });
      if (audioRef.current) { audioRef.current.src = URL.createObjectURL(data); await audioRef.current.play(); }
    } catch (e) { console.warn("[chat] tts playback failed:", e?.message || e); }
  };

  const replaceLast = (patch) => setMessages(m => { const copy = [...m]; copy[copy.length - 1] = patch; return copy; });

  const send = async () => {
    const text = input.trim() || (attachments.selected.length ? "I've attached a file. What can you help me with?" : "");
    if (!text || streaming || attachments.uploading || recording) return;
    const sentFiles = [...attachments.selected];
    setInput("");
    setMessages(m => [...m, { role: "user", content: text, sources: [], attachments: sentFiles }, { role: "assistant", content: "", sources: [], _streaming: true }]);
    setStreaming(true); setOrbState("thinking");
    const draft = { content: "", sources: [], citations: [], confidence: null, steps: [] };
    try {
      await streamChat({
        conversation_id: activeId, message: text,
        attachment_ids: sentFiles.map(file => file.file_id),
        project_id: projectId, ...contextIds, confirm_project_write: allowProjectWrite,
        document_id: docFilter !== "all" ? docFilter : null, mode: voiceMode ? "voice" : "text",
      }, (name, data) => {
        if (name === "meta") setActiveId(data.conversation_id);
        else if (name === "step") { draft.steps = [...draft.steps, data]; replaceLast({ role: "assistant", ...draft, _streaming: true }); }
        else if (name === "sources") { draft.sources = data.sources || []; draft.citations = data.citations || []; draft.confidence = data.confidence || null; }
        else if (name === "token") { draft.content += data.t; replaceLast({ role: "assistant", ...draft, _streaming: true }); }
        else if (name === "done") replaceLast({ role: "assistant", ...draft });
      });
      loadConversations();
      attachments.clear();
      if (voiceMode && draft.content) playTTS(draft.content);
    } catch (e) {
      toast.error("Chat failed: " + (e.message || ""));
      setMessages(m => m.slice(0, -2)); setInput(text);
    } finally { setStreaming(false); setOrbState("idle"); setAllowProjectWrite(false); }
  };

  const exportConversation = async (format) => {
    if (!activeId) { toast.error("Open a saved conversation to export"); return; }
    try {
      const resp = await api.get(`/chat/${activeId}/export.${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: format === "pdf" ? "application/pdf" : "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `conversation-${activeId.slice(0, 8)}.${format}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  const accent = org?.accent_color || "#2563FF";
  const nickname = org?.aria_nickname || "ARIA";
  const last = messages[messages.length - 1];
  const showWelcome = messages.length <= 1 && !streaming && !activeId;

  return (
    <div className="h-[calc(100dvh-49px)] lg:h-dvh flex min-w-0 relative">
      <div className={`${historyOpen ? "flex absolute inset-y-0 left-0 z-30 shadow-2xl" : "hidden"} xl:flex`}>
      <ChatSidebar conversations={conversations} activeId={activeId} search={search} onSearch={setSearch}
        onNew={newConversation} onOpen={openConversation} onDelete={deleteConv} disabled={streaming || attachments.uploading} />
      </div>
      {historyOpen && <button data-testid="chat-history-dismiss" aria-label="Close conversation history" onClick={() => setHistoryOpen(false)} className="xl:hidden absolute inset-0 bg-black/50 z-20" />}

      <div className="flex-1 min-w-0 flex flex-col bg-[var(--aria-bg)]">
        <button type="button" data-testid="chat-history-toggle" title="Conversation history" onClick={() => setHistoryOpen(v => !v)}
          className="xl:hidden flex items-center gap-2 px-3 py-2 text-xs border-b border-[var(--aria-border)] hover:text-[var(--aria-primary)]">{historyOpen ? <X size={14} /> : <History size={14} />} Conversations</button>
        <ChatHeader nickname={nickname} accent={accent} orbState={orbState} streaming={streaming} recording={recording}
          activeId={activeId} flags={flags} docs={docs} docFilter={docFilter} onDocFilter={setDocFilter}
          voiceMode={voiceMode} onToggleVoice={() => setVoiceMode(v => !v)} onExport={exportConversation}
          onOpenConversation={() => setConversationOpen(true)} wakeStatus={wakeStatus} />
        <ProjectChatContext selected={projectId} onSelect={setProjectId} interviewId={contextIds.interview_id} applicationId={contextIds.application_id}
          allowWrite={allowProjectWrite} onWrite={setAllowProjectWrite} message={input} />

        <div className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-6 py-6">
          <div className="max-w-3xl mx-auto space-y-5">
            {showWelcome && <ChatWelcome accent={accent} welcome={org?.welcome_message} onPick={setInput} />}
            {!showWelcome && (
              <MessageList messages={messages} streaming={streaming} accent={accent} activeId={activeId}
                onCite={(name, citations) => setCiteDrawer({ open: true, name, citations })} />
            )}
            <div ref={endRef} />
          </div>
        </div>

        <ChatComposer input={input} onInput={setInput} onSend={send} streaming={streaming} recording={recording}
          onToggleRecording={toggleRecording} nickname={nickname} attachments={attachments} />
        <audio ref={audioRef} hidden />
      </div>

      <ConversationMode isOpen={conversationOpen} onClose={() => setConversationOpen(false)} orgConfig={org}
        projectId={projectId} interviewId={contextIds.interview_id} applicationId={contextIds.application_id}
        wakeWordRequired={false} autoTTS={flags.voice_auto_tts} docFilter={docFilter} activeConversationId={activeId}
        onConversationUpdate={setActiveId} onReload={loadConversations} />

      <WakeWordListener enabled={!!(flags.voice_always_on && flags.voice_wake_word)} paused={conversationOpen || recording}
        onStatus={setWakeStatus}
        onWake={() => { if (conversationOpen) return; toast.success("Hey ARIA — listening", { duration: 1500 }); setConversationOpen(true); }} />

      <ThinkingStage active={streaming && last?.role === "assistant" && (last?.content?.length || 0) < 40}
        partial={last?.role === "assistant" ? last?.content : ""} />

      <CitationDrawer open={citeDrawer.open} onClose={() => setCiteDrawer({ ...citeDrawer, open: false })}
        citations={citeDrawer.citations} focusedName={citeDrawer.name}
        onOpenDocument={(c) => {
          setCiteDrawer(d => ({ ...d, open: false }));
          if (c.project_id) setProjectCitation(c);
          else if (c.source_type === "application") navigate("/app/applications");
          else if (c.source_type === "interview") navigate(`/app/interviews/${c.doc_id}`);
          else setDocReader({ open: true, docId: c.doc_id, citation: c });
        }} />
      <ProjectSourceReader citation={projectCitation} onClose={() => setProjectCitation(null)} />

      <DocumentReader open={docReader.open} docId={docReader.docId} citation={docReader.citation}
        onClose={() => setDocReader({ open: false, docId: null, citation: null })} />
    </div>
  );
}
